import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';
import {
  CreateSelfTicketRequestDto,
  ListSelfTicketRequestsQueryDto,
  ListAvailableItemsQueryDto,
  PaginatedSelfTicketRequestListDto,
  PaginatedAvailableItemsDto,
  PaginatedAvailableCoursesDto,
  PaginatedAvailableCertificationsDto,
  PaginatedAvailableLearningPathsDto,
  SelfTicketRequestResponseDto,
  SelfTicketRequestStatsDto,
} from '@/types/ticket.types';

// Approve request payload
export interface ApproveTicketRequestDto {
  remarks?: string;
  is_mandatory?: boolean;
  deadline_days?: number;
  send_notification?: boolean;
  enable_automatic_reminder?: boolean;
  reminder_frequency_days?: number;
  message?: string;
  // For GENERAL tickets (suggest functionality)
  item_type?: 'COURSE' | 'CERTIFICATION' | 'LEARNING_PATH';
  items?: { item_id: string; item_name: string }[];
  // Item-level approval: only these items are approved; other pending items are denied and the ticket closes
  selected_item_ids?: string[];
}

// Deny request payload
export interface DenyTicketRequestDto {
  denial_reason: string;
}

// Add message payload
export interface AddMessageDto {
  message: string;
}

// Add reaction payload
export interface AddReactionDto {
  emoji: string;
}

// Remove reaction payload
export interface RemoveReactionDto {
  emoji: string;
}

// Delete message payload
export interface DeleteMessageDto {
  delete_type: 'for_everyone' | 'for_me';
}

// Approver dropdown item
export interface ApproverDropdownItem {
  _id: string;
  email: string;
  employee_id: string;
  name: string;
}

// Approver dropdown response
export interface ApproverDropdownResponse {
  data: ApproverDropdownItem[];
}

class TicketService {
  // Create a new self-ticket request
  async createRequest(payload: CreateSelfTicketRequestDto): Promise<SelfTicketRequestResponseDto> {
    const response = await apiClient.post(API_ENDPOINTS.SELF_TICKET_REQUESTS.CREATE, payload);
    return response.data.data;
  }

  // Get list of user's ticket requests with filters
  async getMyRequests(query?: ListSelfTicketRequestsQueryDto): Promise<PaginatedSelfTicketRequestListDto> {
    const response = await apiClient.post(API_ENDPOINTS.SELF_TICKET_REQUESTS.MY_LIST, query || {});
    return response.data.data;
  }

  // Get available courses (excludes already assigned) - returns enriched course data
  async getAvailableCourses(query?: ListAvailableItemsQueryDto): Promise<PaginatedAvailableCoursesDto> {
    const response = await apiClient.post(API_ENDPOINTS.SELF_TICKET_REQUESTS.AVAILABLE_COURSES, query || {});
    return response.data.data;
  }

  // Get available certifications (excludes already assigned) - returns enriched certification data
  async getAvailableCertifications(query?: ListAvailableItemsQueryDto): Promise<PaginatedAvailableCertificationsDto> {
    const response = await apiClient.post(API_ENDPOINTS.SELF_TICKET_REQUESTS.AVAILABLE_CERTIFICATIONS, query || {});
    return response.data.data;
  }

  // Get available learning paths (excludes already assigned) - returns enriched LP data
  async getAvailableLearningPaths(query?: ListAvailableItemsQueryDto): Promise<PaginatedAvailableLearningPathsDto> {
    const response = await apiClient.post(API_ENDPOINTS.SELF_TICKET_REQUESTS.AVAILABLE_LEARNING_PATHS, query || {});
    return response.data.data;
  }

  // Get assigned courses (already assigned to the user)
  async getAssignedCourses(query?: ListAvailableItemsQueryDto): Promise<PaginatedAvailableItemsDto> {
    const response = await apiClient.post(API_ENDPOINTS.SELF_TICKET_REQUESTS.ASSIGNED_COURSES, query || {});
    return response.data.data;
  }

  // Get assigned learning paths (already assigned to the user)
  async getAssignedLearningPaths(query?: ListAvailableItemsQueryDto): Promise<PaginatedAvailableItemsDto> {
    const response = await apiClient.post(API_ENDPOINTS.SELF_TICKET_REQUESTS.ASSIGNED_LEARNING_PATHS, query || {});
    return response.data.data;
  }

  // ============ Approver/Admin Methods ============

  // Get received ticket requests (approver view)
  async getReceivedRequests(query?: ListSelfTicketRequestsQueryDto): Promise<PaginatedSelfTicketRequestListDto> {
    const response = await apiClient.post(API_ENDPOINTS.SELF_TICKET_REQUESTS.RECEIVED_LIST, query || {});
    return response.data.data;
  }

  // Get single ticket request by ID for learner (requester only)
  async getRequestByIdForLearner(id: string): Promise<SelfTicketRequestResponseDto> {
    const response = await apiClient.get(API_ENDPOINTS.SELF_TICKET_REQUESTS.GET_BY_ID(id));
    return response.data.data;
  }

  // Get single ticket request by ID for admin (approver only)
  async getRequestByIdForAdmin(id: string): Promise<SelfTicketRequestResponseDto> {
    const response = await apiClient.get(API_ENDPOINTS.SELF_TICKET_REQUESTS.ADMIN_GET_BY_ID(id));
    return response.data.data;
  }

  // Approve ticket request
  async approveRequest(id: string, payload: ApproveTicketRequestDto): Promise<SelfTicketRequestResponseDto> {
    const response = await apiClient.post(API_ENDPOINTS.SELF_TICKET_REQUESTS.ADMIN_APPROVE(id), payload);
    return response.data.data;
  }

  // Deny ticket request with reason
  async denyRequest(id: string, payload: DenyTicketRequestDto): Promise<SelfTicketRequestResponseDto> {
    const response = await apiClient.post(API_ENDPOINTS.SELF_TICKET_REQUESTS.ADMIN_DENY(id), payload);
    return response.data.data;
  }

  // Mark ticket request as read (Admin/Approver)
  async markAsRead(id: string): Promise<SelfTicketRequestResponseDto> {
    const response = await apiClient.post(API_ENDPOINTS.SELF_TICKET_REQUESTS.ADMIN_MARK_READ(id));
    return response.data.data;
  }

  // Mark messages as read (Learner/Requester)
  async markMessagesAsReadByRequester(id: string): Promise<SelfTicketRequestResponseDto> {
    const response = await apiClient.post(API_ENDPOINTS.SELF_TICKET_REQUESTS.LEARNER_MARK_READ(id));
    return response.data.data;
  }

  // Get request statistics (approver view)
  async getRequestStats(): Promise<SelfTicketRequestStatsDto> {
    const response = await apiClient.get(API_ENDPOINTS.SELF_TICKET_REQUESTS.STATS);
    return response.data.data;
  }

  // Add message to ticket (chat)
  async addMessage(id: string, payload: AddMessageDto): Promise<SelfTicketRequestResponseDto> {
    const response = await apiClient.post(API_ENDPOINTS.SELF_TICKET_REQUESTS.ADD_MESSAGE(id), payload);
    return response.data.data;
  }

  // Delete message (WhatsApp style - delete for everyone or delete for me)
  async deleteMessage(id: string, messageId: string, payload: DeleteMessageDto): Promise<SelfTicketRequestResponseDto> {
    const response = await apiClient.post(API_ENDPOINTS.SELF_TICKET_REQUESTS.DELETE_MESSAGE(id, messageId), payload);
    return response.data.data;
  }

  // Add reaction to message
  async addReaction(id: string, messageId: string, payload: AddReactionDto): Promise<SelfTicketRequestResponseDto> {
    const response = await apiClient.post(API_ENDPOINTS.SELF_TICKET_REQUESTS.ADD_REACTION(id, messageId), payload);
    return response.data.data;
  }

  // Remove reaction from message
  async removeReaction(id: string, messageId: string, payload: RemoveReactionDto): Promise<SelfTicketRequestResponseDto> {
    const response = await apiClient.post(API_ENDPOINTS.SELF_TICKET_REQUESTS.REMOVE_REACTION(id, messageId), payload);
    return response.data.data;
  }

  // Clear chat (soft clear for current user)
  async clearChat(id: string): Promise<SelfTicketRequestResponseDto> {
    const response = await apiClient.post(API_ENDPOINTS.SELF_TICKET_REQUESTS.CLEAR_CHAT(id));
    return response.data.data;
  }

  // Close ticket (after approval or denial)
  async closeTicket(id: string): Promise<SelfTicketRequestResponseDto> {
    const response = await apiClient.post(API_ENDPOINTS.SELF_TICKET_REQUESTS.CLOSE_TICKET(id));
    return response.data.data;
  }

  // Remove an approver from a self-ticket request (Admin)
  async removeApprover(id: string, userId: string): Promise<SelfTicketRequestResponseDto> {
    const response = await apiClient.delete(`/api/self-ticket-requests/admin/${id}/approver/${userId}`);
    return response.data.data;
  }

  // Get approvers dropdown (unique BU Heads, RMs, HRBPs)
  async getApproversDropdown(): Promise<ApproverDropdownResponse> {
    const response = await apiClient.get(API_ENDPOINTS.SELF_TICKET_REQUESTS.APPROVERS_DROPDOWN);
    return response.data.data;
  }
}

export const ticketService = new TicketService();
